import { create } from "zustand";
import produce from "immer";
import * as api from "../services/work_orders";
import { arrayToByIdsAllIds, createLatestRequestTracker } from "./utils";
import i18n from "../translations/i18n";
import { showApiError } from "../utils/apiError";
import { LIST_SECTION, useListPreferencesStore } from "./list_preferences";

export const WORK_ORDER_FILTER = Object.freeze({
  MINE: "mine",
  UNASSIGNED: "unassigned",
  OPEN: "open",
  IN_PROGRESS: "in_progress",
  ACTIVE: "active",
  DONE: "done",
  CANCELED: "canceled",
  ALL: "all",
});

export const WORK_ORDER_STATUS = Object.freeze({
  OPEN: "open",
  IN_PROGRESS: "in_progress",
  DONE: "done",
  CANCELED: "canceled",
});

export const FILE_EXTRACTION_STATUS = Object.freeze({
  PENDING: "pending",
  DONE: "done",
  FAILED: "failed",
});

export const WORK_ORDER_DUE_FILTER = Object.freeze({
  DUE: "due",
  OVERDUE: "overdue",
  NOT_DUE: "not_due",
});

const trackListRequest = createLatestRequestTracker();
const trackZipOptionsRequest = createLatestRequestTracker();
const trackMapRequest = createLatestRequestTracker();
const trackNeighborsRequest = createLatestRequestTracker();

const NO_NEIGHBORS = Object.freeze({ id: null, prevId: null, nextId: null });

const EMPTY_MAP_STATS = Object.freeze({ total: 0, located: 0, unlocated: 0 });

export const useWorkOrdersStore = create((set, get) => ({
  byIds: {},
  allIds: [],
  total: 0,
  totalMine: 0,
  totalUnassigned: 0,
  totalOpen: 0,
  lastFetchParams: null,
  neighbors: NO_NEIGHBORS,
  fetchNeighbors: async ({ id }) => {
    // Only the in-memory params of this tab's last list know its search;
    // after a reload the remembered list settings take over.
    const listParams =
      get().lastFetchParams ??
      useListPreferencesStore.getState().preferences[LIST_SECTION.WORK_ORDERS] ??
      {};
    const isLatestRequest = trackNeighborsRequest();
    const result = await api.getNeighbors({ ...listParams, id });
    if (!isLatestRequest()) return result;
    set(
      produce((draft) => {
        draft.neighbors = {
          id,
          prevId: result.data?.prev_id ?? null,
          nextId: result.data?.next_id ?? null,
        };
      }),
    );
    if (result.error) showApiError(result.error, i18n.t.bind(i18n));
    return result;
  },
  zipOptions: [],
  mapItems: [],
  mapStats: EMPTY_MAP_STATS,
  mapLoading: false,
  fetchSome: async ({
    q = "",
    filter = "",
    due = "",
    assigned_to_id = "",
    zip_prefix = [],
    limit = 20,
    offset = 0,
    sort_by = "",
    sort_dir = "",
  }) => {
    const params = {
      q,
      filter,
      due,
      assigned_to_id,
      zip_prefix,
      limit,
      offset,
      sort_by,
      sort_dir,
    };
    const isLatestRequest = trackListRequest();
    const some = await api.getSome(params);
    if (!isLatestRequest()) return some;
    set(
      produce((draft) => {
        [draft.byIds, draft.allIds] = arrayToByIdsAllIds(some.data);
        draft.total = some.total;
        draft.lastFetchParams = params;
        if (filter === WORK_ORDER_FILTER.MINE) draft.totalMine = some.total;
        if (filter === WORK_ORDER_FILTER.UNASSIGNED) draft.totalUnassigned = some.total;
        if (filter === WORK_ORDER_FILTER.OPEN) draft.totalOpen = some.total;
      }),
    );
    if (some.error) {
      showApiError(some.error, i18n.t.bind(i18n));
    }
    return some;
  },
  fetchZipOptions: async ({ q = "", filter = "", due = "", assigned_to_id = "" }) => {
    const isLatestRequest = trackZipOptionsRequest();
    const result = await api.getZipOptions({ q, filter, due, assigned_to_id });
    if (!isLatestRequest()) return result;
    set(produce((draft) => { draft.zipOptions = result.data; }));
    if (result.error) showApiError(result.error, i18n.t.bind(i18n));
    return result;
  },
  fetchMap: async ({ q = "", filter = "", due = "", assigned_to_id = "", zip_prefix = [] }) => {
    const isLatestRequest = trackMapRequest();
    set(produce((draft) => { draft.mapLoading = true; }));
    const result = await api.getMap({ q, filter, due, assigned_to_id, zip_prefix });
    if (!isLatestRequest()) return result;
    set(
      produce((draft) => {
        draft.mapItems = result.data?.data ?? [];
        draft.mapStats = result.data
          ? {
              total: result.data.total,
              located: result.data.located,
              unlocated: result.data.unlocated,
            }
          : EMPTY_MAP_STATS;
        draft.mapLoading = false;
      }),
    );
    if (result.error) showApiError(result.error, i18n.t.bind(i18n));
    return result;
  },
  // Picker search for linking: only active work orders are link targets.
  searchSome: async ({ q = "", limit = 10, offset = 0 }) => {
    return api.getSome({ q, limit, offset, filter: WORK_ORDER_FILTER.ACTIVE });
  },
  fetchMineTotal: async () => {
    const some = await api.getSome({ filter: WORK_ORDER_FILTER.MINE, limit: 1 });
    set(produce((draft) => { draft.totalMine = some.total; }));
    return some;
  },
  fetchUnassignedTotal: async () => {
    const some = await api.getSome({ filter: WORK_ORDER_FILTER.UNASSIGNED, limit: 1 });
    set(produce((draft) => { draft.totalUnassigned = some.total; }));
    return some;
  },
  fetchOpenTotal: async () => {
    const some = await api.getSome({ filter: WORK_ORDER_FILTER.OPEN, limit: 1 });
    set(produce((draft) => { draft.totalOpen = some.total; }));
    return some;
  },
  getById: async ({ id }) => {
    const one = await api.getById({ id });
    set(
      produce((draft) => {
        draft.byIds[one.data.id] = one.data;
        if (!draft.allIds.includes(one.data.id)) {
          draft.allIds.push(one.data.id);
        }
      }),
    );
    return one;
  },
  createOne: async (payload) => {
    const one = await api.createOne(payload);
    if (one.data) {
      set(
        produce((draft) => {
          draft.byIds[one.data.id] = one.data;
          if (!draft.allIds.includes(one.data.id)) {
            draft.allIds.unshift(one.data.id);
          }
        }),
      );
    }
    if (one.error) showApiError(one.error, i18n.t.bind(i18n));
    return one;
  },
  updateOne: async (payload) => {
    const one = await api.updateOne(payload);
    if (one.data) {
      set(produce((draft) => { draft.byIds[one.data.id] = one.data; }));
    }
    if (one.error) showApiError(one.error, i18n.t.bind(i18n));
    return one;
  },
  // No showApiError here: the assistant dialog renders the error inline.
  extractFromText: async ({ text }) => api.extractFromText({ text }),
  assignWorkOrder: async ({ id, assigned_to_id }) => {
    const one = await api.assignWorkOrder({ id, assigned_to_id });
    if (one.data) {
      set(produce((draft) => { draft.byIds[one.data.id] = one.data; }));
    }
    if (one.error) showApiError(one.error, i18n.t.bind(i18n));
    return one;
  },
  assignMe: async ({ id }) => {
    const one = await api.assignMe({ id });
    if (one.data) {
      set(produce((draft) => { draft.byIds[one.data.id] = one.data; }));
    }
    if (one.error) showApiError(one.error, i18n.t.bind(i18n));
    return one;
  },
  cancelWorkOrder: async ({ id }) => {
    const one = await api.cancelWorkOrder({ id });
    if (one.data) {
      set(produce((draft) => { draft.byIds[one.data.id] = one.data; }));
    }
    if (one.error) showApiError(one.error, i18n.t.bind(i18n));
    return one;
  },
  linkDeliveryNote: async ({ id, delivery_note_id }) => {
    const one = await api.linkDeliveryNote({ id, delivery_note_id });
    if (one.data) {
      set(produce((draft) => { draft.byIds[one.data.id] = one.data; }));
    }
    if (one.error) showApiError(one.error, i18n.t.bind(i18n));
    return one;
  },
  unlinkDeliveryNote: async ({ id, deliveryNoteId }) => {
    const one = await api.unlinkDeliveryNote({ id, deliveryNoteId });
    if (one.data) {
      set(produce((draft) => { draft.byIds[one.data.id] = one.data; }));
    }
    if (one.error) showApiError(one.error, i18n.t.bind(i18n));
    return one;
  },
  deleteFile: async ({ id, fileId }) => {
    const result = await api.deleteFile({ id, fileId });
    if (!result.error) {
      set(
        produce((draft) => {
          if (draft.byIds[id]) {
            draft.byIds[id].files = (draft.byIds[id].files || []).filter(
              (f) => f.id !== fileId,
            );
          }
        }),
      );
    }
    if (result.error) showApiError(result.error, i18n.t.bind(i18n));
    return result;
  },
  addComment: async ({ id, text }) => {
    const result = await api.addComment({ id, text });
    if (result.data) {
      set(
        produce((draft) => {
          if (draft.byIds[id]) {
            draft.byIds[id].comments = draft.byIds[id].comments || [];
            draft.byIds[id].comments.push(result.data);
          }
        }),
      );
    }
    if (result.error) showApiError(result.error, i18n.t.bind(i18n));
    return result;
  },
  updateComment: async ({ id, commentId, text }) => {
    const result = await api.updateComment({ id, commentId, text });
    if (result.data) {
      set(
        produce((draft) => {
          if (draft.byIds[id]) {
            const comments = draft.byIds[id].comments || [];
            const idx = comments.findIndex((c) => c.id === commentId);
            if (idx !== -1) {
              comments[idx] = result.data;
            }
          }
        }),
      );
    }
    if (result.error) showApiError(result.error, i18n.t.bind(i18n));
    return result;
  },
  deleteComment: async ({ id, commentId }) => {
    const result = await api.deleteComment({ id, commentId });
    if (!result.error) {
      set(
        produce((draft) => {
          if (draft.byIds[id]) {
            draft.byIds[id].comments = (draft.byIds[id].comments || []).filter(
              (c) => c.id !== commentId,
            );
          }
        }),
      );
    }
    if (result.error) showApiError(result.error, i18n.t.bind(i18n));
    return result;
  },
  removeFromList: (id) => {
    set(
      produce((draft) => {
        draft.allIds = draft.allIds.filter((i) => i !== id);
        delete draft.byIds[id];
        draft.total = Math.max(0, draft.total - 1);
      }),
    );
  },
}));
